/* eslint-disable sonarjs/no-duplicate-string */
import { InputWithLabel, SubmitHandler } from '@visto-tech/forms';
import { PricingTable } from 'components/Stripe/Pricing/PricingTable';
import { GoogleTranslateSelect } from 'components/Translate/GoogleTranslate';
import Account from 'dataLayer/Account';
import AnimateGreenCheck from 'elements/Animations/AnimateGreenCheck';
import Button from 'elements/Button';
import ErrorDisplay from 'elements/ErrorDisplay/ErrorDisplay';
import Loading from 'elements/Loading';
import Text from 'elements/Text';
import { YoutubeIframeModal } from 'elements/Video/YoutubeIframe';
import { useActiveUser } from 'hooks/useActiveUser';
import { useFormManager } from 'hooks/useFormManager';
import { useLayout } from 'hooks/useLayout';
import { isNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { BookOpen, Briefcase, ChevronRight, Globe, User } from 'react-feather';
import toast from 'react-hot-toast';
import { redirectUser, scrollTo } from 'utils/helpers';
import * as yup from 'yup';

const FORM_DATA = {
  email: '',
  password: '',
  confirm_password: '',
  first_name: '',
  last_name: '',
};

export const RegisterFullSuite = () => {
  useLayout({ layout: 'Base', title: 'Register' });

  const user = useActiveUser();

  const recaptchaSiteKey =
    process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY ?? null;

  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checkedTerms, setCheckedTerms] = useState(false);
  const [hasRegistered, setHasRegistered] = useState(false);
  const [type, setType] = useState<'PROFESSIONAL' | 'PERSONAL' | null>(null);

  const formManager = useFormManager({
    formData: FORM_DATA,
    validationSchema: yup.object({
      email: yup.string().required('A email is required to register'),
      password: yup.string().required('A password is required to register'),
      confirm_password: yup
        .string()
        .required('A password confirmation is required to register'),
      first_name: yup.string().required('A first name is required to register'),
      last_name: yup.string().required('A last name is required to register'),
    }),
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;

    script.addEventListener('load', () => {
      setIsRecaptchaLoaded(true);
    });

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit: SubmitHandler<typeof FORM_DATA> = async (_, data) => {
    const targetWindow = window as any;
    const recaptcha = targetWindow.grecaptcha;

    recaptcha.ready(() => {
      recaptcha
        .execute(recaptchaSiteKey, {
          action: 'submit',
        })
        .then(async (token: string) => {
          await handleRegister({ data, token });
        });
    });
  };

  const handleRegister = async ({
    data,
    token,
  }: {
    data: typeof FORM_DATA;
    token: string;
  }) => {
    setIsLoading(true);

    formManager.clearErrors();

    const registered = await Account.publicSignUp({
      email: data?.email,
      password: data?.password,
      confirmPassword: data?.confirm_password,
      firstName: data?.first_name,
      lastName: data?.last_name,
      recaptchaToken: token,
    });

    if (!registered?.success) {
      toast.error('Failed to register');

      setIsLoading(false);

      registered?.errorMessage &&
        formManager.addError(registered?.errorMessage);

      return null;
    }

    toast.success('Successfully registered');

    setHasRegistered(true);
    setIsLoading(false);

    return null;
  };

  if (isNumber(user?.id)) {
    redirectUser('/');
  }

  if (!isRecaptchaLoaded) {
    return <Loading.Default />;
  }

  let Content = (
    <>
      <Text.Heading variant="form-heading" className="text-center">
        Sign up for Visto
      </Text.Heading>
      <Text.Paragraph className="text-center fs18">
        Please select an option below to continue
      </Text.Paragraph>
      <div className="mx-auto w-52 h-[1px] bg-gray-400 my-4"></div>
      <Text.Paragraph className="text-center fs18 font-bold mb-2">
        I am a:
      </Text.Paragraph>
      <div className="space-y-2">
        <button
          onClick={() => setType('PERSONAL')}
          className="flex items-center justify-between border border-gray-200 rounded-md p-4 w-full hover:bg-gray-100"
        >
          <div className="flex items-center">
            <BookOpen className="mr-3" />
            <Text.Paragraph className="font-semibold">
              International Student
            </Text.Paragraph>
          </div>
          <ChevronRight className="text-gray-500" />
        </button>
        <button
          onClick={() => setType('PERSONAL')}
          className="flex items-center justify-between border border-gray-200 rounded-md p-4 w-full hover:bg-gray-100"
        >
          <div className="flex items-center">
            <Globe className="mr-3" />
            <Text.Paragraph className="font-semibold">
              Individual wanting to Immigrate to Canada
            </Text.Paragraph>
          </div>
          <ChevronRight className="text-gray-500" />
        </button>
        <button
          onClick={() => setType('PROFESSIONAL')}
          className="flex items-center justify-between border border-gray-200 rounded-md p-4 w-full hover:bg-gray-100"
        >
          <div className="flex items-center">
            <Briefcase className="mr-3" />
            <Text.Paragraph className="font-semibold">
              Immigration Lawyer
            </Text.Paragraph>
          </div>
          <ChevronRight className="text-gray-500" />
        </button>
        <button
          onClick={() => setType('PROFESSIONAL')}
          className="flex items-center justify-between border border-gray-200 rounded-md p-4 w-full hover:bg-gray-100"
        >
          <div className="flex items-center">
            <User className="mr-3" />
            <Text.Paragraph className="font-semibold">
              Immigration Consultant
            </Text.Paragraph>
          </div>
          <ChevronRight className="text-gray-500" />
        </button>
      </div>
    </>
  );

  if (type === 'PROFESSIONAL') {
    Content = (
      <div id="register-form">
        <Text.Heading variant="form-heading" className="text-center">
          Sign up for Visto
        </Text.Heading>
        <form
          onSubmit={formManager.createSubmitHandler(handleSubmit)}
          autoComplete="off"
        >
          <ErrorDisplay formManager={formManager} />
          <InputWithLabel.WithFormManager
            id="first_name"
            formFieldSetProps={{
              className: 'mt-4',
            }}
            name="first_name"
            formManager={formManager}
            placeholder="First name"
          >
            First name
          </InputWithLabel.WithFormManager>
          <InputWithLabel.WithFormManager
            id="last_name"
            formFieldSetProps={{
              className: 'mt-4',
            }}
            name="last_name"
            formManager={formManager}
            placeholder="Last name"
          >
            Last name
          </InputWithLabel.WithFormManager>
          <InputWithLabel.WithFormManager
            id="email"
            formFieldSetProps={{
              className: 'mt-4',
            }}
            name="email"
            inputProps={{ type: 'email' }}
            formManager={formManager}
            placeholder="Email"
          >
            Email
          </InputWithLabel.WithFormManager>
          <InputWithLabel.WithFormManager
            id="password"
            formFieldSetProps={{
              className: 'mt-4',
            }}
            name="password"
            formManager={formManager}
            inputProps={{ type: 'password' }}
          >
            Password
          </InputWithLabel.WithFormManager>
          <InputWithLabel.WithFormManager
            id="confirm_password"
            formFieldSetProps={{
              className: 'mt-4',
            }}
            name="confirm_password"
            formManager={formManager}
            inputProps={{ type: 'password' }}
          >
            Confirm password
          </InputWithLabel.WithFormManager>
          <div className="flex items-center mt-5">
            <input
              type="checkbox"
              id="acceptTerms"
              name="acceptTerms"
              onChange={() => setCheckedTerms(!checkedTerms)}
              className="rounded-sm border-2 border-black mr-4 w-5 h-5"
            />
            <label className="fs14" htmlFor="acceptTerms">
              By signing up, I accept the{' '}
              <a
                target="_blank"
                href="/legal/terms-of-service"
                className="underline"
              >
                Terms of Service
              </a>
              ,{' '}
              <a
                target="_blank"
                href="/legal/privacy-policy"
                className="underline"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                target="_blank"
                href="/legal/cookie-policy"
                className="underline"
              >
                Cookies Policy
              </a>
              .
            </label>
          </div>
          <Button
            type="submit"
            disabled={isLoading || !checkedTerms}
            className="mt-6 mx-auto"
            size="large"
            onClick={() => formManager.clearErrors()}
            isLoading={isLoading}
          >
            Create Account
          </Button>
          {!checkedTerms && (
            <Text.Paragraph className="text-center fs12 text-red-600 font-semibold mt-3">
              Accept terms above to continue
            </Text.Paragraph>
          )}
        </form>
      </div>
    );
  }

  if (type === 'PERSONAL') {
    Content = (
      <div id="register-form">
        <Text.Heading variant="form-heading" className="text-center">
          Thank you!
        </Text.Heading>
        <Text.Paragraph className="text-center mb-2">
          Unfortunately, right now, Visto is only for immigration professionals,
          and we do not help individual apply to come to Canada.
        </Text.Paragraph>
      </div>
    );
  }

  if (hasRegistered) {
    return (
      <div className="visto-component max-w-2xl mx-auto">
        <div className="flex justify-center mb-5">
          <AnimateGreenCheck />
        </div>
        <Text.Heading variant="form-heading" className="text-center mb-3">
          Sign-up Successful!
        </Text.Heading>
        <Text.Paragraph className="text-center mb-2">
          Thank you for signing up!
        </Text.Paragraph>
        <Text.Paragraph className="text-center mb-5">
          You are almost done, please{' '}
          <strong>check your email to verify your account</strong> to start
          using Visto!
        </Text.Paragraph>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1250px] space-y-14">
      <div>
        <img
          src="/bot-avatar.png"
          alt="VistoBot"
          className="mb-5 w-20 mx-auto"
        />
        <Text.Heading className="font-extrabold text-center">
          Get started with{' '}
          <span className="text-visto-light-blue">Visto AI</span>
        </Text.Heading>
        <Text.Heading as="h2" className="text-center mb-4 fs21" variant="none">
          Prepare immigration applications faster than ever before.
        </Text.Heading>
        <Text.Paragraph className="mb-2 md:fs18 text-center max-w-2xl mx-auto">
          Simplify your file preparation with our advanced automation and AI
          technology.
        </Text.Paragraph>
      </div>

      <div className="bg-white rounded-lg border border-visto-gray p-6 md:p-10 box-border text-visto max-w-2xl mx-auto">
        {Content}
        <div className="flex justify-center mt-4">
          <GoogleTranslateSelect />
        </div>
      </div>

      <div className="visto-component">
        <Text.Heading
          as="h2"
          className="text-center mb-8 text-3xl"
          variant="none"
        >
          Pricing
        </Text.Heading>
        <PricingTable hideCta />
      </div>

      <div className="mx-auto max-w-2xl pb-8">
        <Text.Heading
          as="h2"
          className="text-center mb-1 text-3xl"
          variant="none"
        >
          Watch Visto AI in action
        </Text.Heading>
        <Text.Paragraph className="fs18 text-center mb-6">
          The Visto propriety immigration software and AI streamlines your
          application preparation, so you’re not wasting time on manual tasks.
        </Text.Paragraph>
        <YoutubeIframeModal
          url={'https://www.youtube.com/embed/72w3ISSl3w0?si=Zra7q4nPUUDAb8th'}
        />
        <Button
          className="mx-auto mt-8"
          size="large"
          onClick={() => scrollTo('#register-form')}
        >
          Sign up now
        </Button>
      </div>
    </div>
  );
};
